import { inject, Injectable } from '@angular/core';
import { AssocControlPointBase } from '../../models/assoc-control-point-base';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { ActionCode } from '../../../../../../core/models/resources/action-code';
import { ApplicationStateService } from '../../../../shared/services/application-state/application-state.service';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { ApplicationFormControlManagerService } from '../../../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { SchedulesDAndHSharedFormService } from '../../../shared/services/schedules-d-and-h-shared-form/schedules-d-and-h-shared-form.service';

@Injectable({
    providedIn: 'root',
})
export class ControlPointFormService {
    private readonly controlsDisabledForDeletedControlPoints: (keyof AssocControlPointBase)[] =
        ['streetAddress', 'city', 'county', 'stateCode', 'phone'];

    private readonly applicationStateService = inject(ApplicationStateService);
    private readonly applicationFormControlManager = inject(
        ApplicationFormControlManagerService,
    );
    private readonly schedulesDAndHSharedFormService = inject(
        SchedulesDAndHSharedFormService,
    );

    getFormDefinition(): FormGroup<FormGroupType<AssocControlPointBase>> {
        return new FormGroup<FormGroupType<AssocControlPointBase>>({
            id: new FormControl<number | null>(null),
            actionCode: new FormControl<ActionCode | null>(
                ActionCode.Add,
                this.applicationStateService.IsExistingLicenseApplication()
                    ? undefined
                    : Validators.required,
            ),
            number: new FormControl<number | null>(null, Validators.required),
            streetAddress: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(80),
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
            city: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(20),
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
            county: new FormControl<string | null>(null, [
                Validators.maxLength(20),
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
            stateCode: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            phone: new FormControl<string | null>(null, [
                Validators.required,
                Validators.minLength(10),
                Validators.maxLength(10),
            ]),
            isDeleted: new FormControl<boolean | null>(null),
        });
    }

    /**
     * Toggles the form controls based on the isDeleted value.
     * @param controlPoint the control point form group to update
     * @param toggleOptions options for enabling/disabling controls
     */
    toggleFormControlsForDeletedChange(
        controlPoint: FormGroup<FormGroupType<AssocControlPointBase>>,
        toggleOptions: { emitEvent?: boolean } = { emitEvent: true },
    ): void {
        const isDeleted = controlPoint.controls.isDeleted.value;
        this.controlsDisabledForDeletedControlPoints.forEach((controlName) => {
            const control = controlPoint.controls[controlName];

            if (isDeleted === true) {
                this.applicationFormControlManager.disable(
                    control,
                    toggleOptions,
                );
            } else {
                this.applicationFormControlManager.enable(
                    control,
                    toggleOptions,
                    () => {
                        control.markAsTouched();
                    },
                );
            }
        });
    }

    /**
     * Marks the control point as deleted when applicable and toggles the controls accordingly to it's deleted state.
     * @param controlPoint the control point form group to update
     * @param response the response containing the action codes for the control points
     * @returns
     */
    markControlPointAsDeletedAndToggleControls(
        controlPoint: FormGroup<FormGroupType<AssocControlPointBase>>,
        response: AssocControlPointBase[],
    ): void {
        // Don't need to toggle anything if the control point is new
        // also when the deleted control has not been touched by the user and the control point is not deleted
        if (
            controlPoint.controls.actionCode.value === ActionCode.Add ||
            (controlPoint.controls.isDeleted.pristine &&
                controlPoint.controls.isDeleted.value !== true)
        ) {
            return;
        }

        this.schedulesDAndHSharedFormService.refreshFormActionCode(
            controlPoint,
            response,
        );

        this.toggleFormControlsForDeletedChange(controlPoint);
    }
}
